import { useEffect, useRef, useState } from 'react';
import Button from '@/components/base/Button';
import { serviceDetails } from '@/mocks/services';

type Service = (typeof serviceDetails)[number];

function ServiceText({ s, headingLevel = 'h2' }: { s: Service; headingLevel?: 'h2' | 'h3' }) {
  const Heading = headingLevel;
  return (
    <>
      <p className="text-xs md:text-[13px] font-medium tracking-[0.14em] text-secondary-500">{s.num} — {s.name.toUpperCase()}</p>
      <Heading className="mt-4 font-heading text-[28px] md:text-[36px] lg:text-[44px] leading-[1.12] font-medium tracking-tight">{s.headline}</Heading>
      <p className="mt-5 text-base md:text-lg leading-[1.6] text-foreground-600">{s.copy}</p>
      <ul className="mt-8 border-t border-background-300">
        {s.highlights.map((h) => (
          <li key={h} className="flex items-center gap-3 py-3.5 border-b border-background-300 text-[15px] text-foreground-900">
            <span className="w-6 h-6 rounded-full bg-background-50 text-secondary-500 flex items-center justify-center shrink-0"><i className="ri-check-line text-sm" /></span>
            {h}
          </li>
        ))}
      </ul>
      <Button href={`/services/${s.slug}`} className="mt-8">{s.cta} <i className="ri-arrow-right-line" /></Button>
    </>
  );
}

function StickyStory() {
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState<boolean[]>(() => serviceDetails.map((_, i) => i === 0));
  const chapterRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const els = chapterRefs.current.filter((el): el is HTMLElement => !!el);
    if (!els.length || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number((entry.target as HTMLElement).dataset.index);
            if (!Number.isNaN(idx)) setActive(idx);
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Only show an image once it's decoded; otherwise keep the last ready one visible.
  const shown = loaded[active] ? active : loaded.findIndex(Boolean);

  return (
    <div className="svc-story hidden lg:grid grid-cols-[48fr_52fr] gap-20 items-stretch">
      <div className="self-stretch relative">
      <div className="svc-sticky-stage sticky top-[96px] h-[70vh] max-h-[calc(100vh-128px)]">
      <div className="relative w-full h-full rounded-[20px] overflow-hidden bg-background-200">
        {serviceDetails.map((s, i) => (
          <img
            key={s.slug}
            src={s.overviewImage}
            alt={`${s.name} by CLEARY`}
            title={`${s.name} — CLEARY home cleaning`}
            loading="eager"
            decoding="async"
            onLoad={() => setLoaded((prev) => (prev[i] ? prev : prev.map((v, j) => (j === i ? true : v))))}
            className={`svc-stage-img absolute inset-0 w-full h-full object-cover object-top ${i === shown ? 'is-active' : ''}`}
            aria-hidden={i !== shown}
          />
        ))}
        <div className="absolute left-5 bottom-5 flex gap-2" aria-hidden="true">
          {serviceDetails.map((s, i) => (
            <span key={s.slug} className={`h-1.5 rounded-full transition-all duration-500 ${i === active ? 'w-8 bg-background-50' : 'w-1.5 bg-background-50/60'}`} />
          ))}
        </div>
      </div>
      </div>
      </div>
      <div>
        {serviceDetails.map((s, i) => (
          <article
            key={s.slug}
            ref={(el) => { chapterRefs.current[i] = el; }}
            data-index={i}
            className={`svc-chapter min-h-[80vh] flex flex-col justify-center py-16 ${i === active ? 'is-active' : ''}`}
          >
            <ServiceText s={s} />
          </article>
        ))}
      </div>
    </div>
  );
}

export default function ServiceCollection() {
  return (
    <section id="service-collection" className="px-4 md:px-6 lg:px-20 py-16 md:py-20 lg:py-[72px] bg-background-100">
      <div className="max-w-content mx-auto">
        <StickyStory />
        <div className="lg:hidden flex flex-col gap-16 md:gap-24">
          {serviceDetails.map((s, i) => (
            <article key={s.slug} className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">
              <div className={`w-full h-[360px] md:h-[480px] rounded-[20px] overflow-hidden bg-background-200 ${i % 2 === 1 ? 'md:order-2' : ''}`}>
                <img src={s.overviewImage} alt={`${s.name} by CLEARY`} title={`${s.name} — CLEARY home cleaning`} loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
              </div>
              <div>
                <ServiceText s={s} headingLevel="h3" />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}